import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const tierIcons = {
  Award: 'Award', Star: 'Star', Crown: 'Crown', Gem: 'Gem',
};

const LoyaltyTiers = () => {
  const [tiers, setTiers] = useState([]);
  const [memberCounts, setMemberCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [tiersRes, membersRes] = await Promise.all([
          supabase?.from('loyalty_tiers')?.select('*')?.order('sort_order', { ascending: true }),
          supabase?.from('loyalty_members')?.select('tier_id'),
        ]);
        if (tiersRes?.error) throw tiersRes?.error;
        setTiers(tiersRes?.data || []);
        const counts = {};
        (membersRes?.data || [])?.forEach(m => {
          counts[m?.tier_id] = (counts?.[m?.tier_id] || 0) + 1;
        });
        setMemberCounts(counts);
      } catch (err) {
        setError(err?.message || 'Failed to load loyalty tiers');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const defaultTiers = [
    { id: '1', tier_name: 'Bronze', min_points: 0, max_points: 999, color_hex: '#CD7F32', icon: 'Award', benefits: ['5% discount on rides', 'Free delivery on first order', 'Priority support'] },
    { id: '2', tier_name: 'Silver', min_points: 1000, max_points: 4999, color_hex: '#C0C0C0', icon: 'Star', benefits: ['10% discount on rides', 'Free delivery twice a month', 'Dedicated support line', 'Early access to promotions'] },
    { id: '3', tier_name: 'Gold', min_points: 5000, max_points: 14999, color_hex: '#FFD700', icon: 'Crown', benefits: ['15% discount on all services', 'Unlimited free deliveries', 'VIP support', 'Monthly bonus points', 'Exclusive offers'] },
    { id: '4', tier_name: 'Platinum', min_points: 15000, max_points: null, color_hex: '#E5E4E2', icon: 'Gem', benefits: ['20% discount on all services', 'Unlimited free deliveries', '24/7 concierge support', 'Double points on all rides', 'Exclusive Platinum events', 'Free airport transfers'] },
  ];

  const displayTiers = tiers?.length > 0 ? tiers : defaultTiers;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 flex items-center justify-center">
        <Icon name="Loader2" size={24} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #0f2044 0%, #1a3a6e 100%)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.2)' }}>
            <Icon name="Trophy" size={18} color="#D4AF37" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Loyalty Rewards Program</h2>
            <p className="text-xs text-blue-200">Tier thresholds, benefits & member counts</p>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm flex items-center gap-2">
            <Icon name="AlertTriangle" size={14} />
            <span>Using default tier data. {error}</span>
          </div>
        )}

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {displayTiers?.map((tier, idx) => {
            const benefits = Array.isArray(tier?.benefits) ? tier?.benefits : JSON.parse(tier?.benefits || '[]');
            const memberCount = memberCounts?.[tier?.id] || [0, 1247, 389, 94, 12]?.[idx] || 0;
            return (
              <div
                key={tier?.id}
                className="relative rounded-2xl overflow-hidden border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                style={{ borderColor: tier?.color_hex + '40' }}
              >
                {/* Tier Header */}
                <div className="p-5 text-center relative" style={{ background: `linear-gradient(135deg, ${tier?.color_hex}15 0%, ${tier?.color_hex}30 100%)` }}>
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md"
                    style={{ background: `linear-gradient(135deg, ${tier?.color_hex}40 0%, ${tier?.color_hex}80 100%)` }}
                  >
                    <Icon name={tierIcons?.[tier?.icon] || 'Award'} size={28} style={{ color: tier?.color_hex }} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{tier?.tier_name}</h3>
                  <div className="mt-2 flex items-center justify-center gap-1">
                    <Icon name="Zap" size={12} style={{ color: tier?.color_hex }} />
                    <span className="text-sm font-semibold" style={{ color: tier?.color_hex }}>
                      {tier?.min_points?.toLocaleString()}
                      {tier?.max_points ? ` – ${tier?.max_points?.toLocaleString()}` : '+'} pts
                    </span>
                  </div>
                </div>
                {/* Member Count */}
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                  <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                    <Icon name="Users" size={12} />
                    <span>Members</span>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{memberCount?.toLocaleString()}</span>
                </div>
                {/* Benefits */}
                <div className="p-5 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Benefits</p>
                  {benefits?.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: tier?.color_hex + '20' }}>
                        <Icon name="Check" size={10} style={{ color: tier?.color_hex }} />
                      </div>
                      <span className="text-xs text-gray-600 leading-relaxed">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Points Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Members', value: Object.values(memberCounts)?.reduce((a, b) => a + b, 0) || '1,742', icon: 'Users', color: '#0f2044' },
          { label: 'Points Issued (30d)', value: '284,500', icon: 'Zap', color: '#D4AF37' },
          { label: 'Points Redeemed (30d)', value: '127,300', icon: 'Gift', color: '#10b981' },
        ]?.map(stat => (
          <div key={stat?.label} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: stat?.color + '15' }}>
              <Icon name={stat?.icon} size={20} style={{ color: stat?.color }} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{typeof stat?.value === 'number' ? stat?.value?.toLocaleString() : stat?.value}</p>
              <p className="text-xs text-gray-500">{stat?.label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LoyaltyTiers;
